<script lang="ts" setup>
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import Fuse from 'fuse.js/basic';
import { storeToRefs } from 'pinia';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { useCommandModalStore } from '#ui-shared/components/modals/command-modal/command-modal-store';
import type { Command, CommandPlugin } from '#ui-shared/components/modals/command-modal/command-modal.types';

interface CommandModalProps {
  modalId: string;
  plugins?: CommandPlugin[];
}

const props = defineProps<CommandModalProps>();
const { t } = useI18n();
const { plugins } = toRefs(props);
const query = ref('');

const commandModalStore = useCommandModalStore();
const { commands } = storeToRefs(commandModalStore);
const isModalVisible = computed<boolean>(() => {
  return commandModalStore.isCommandModalOpen(props.modalId);
});

// These commands are visible before a user has entered anything, in case we want populated commands prior to writing anything.
const emptyQueryCommands: Command[] = [
  {
    id: 'closeMenu',
    title: t('commands.closeMenu'),
    action: () => {
      // Doing nothing still makes it close!
    },
  },
];

// Commands returned after writing a query
const commandResults = computed(() => {
  const fuse = new Fuse(Array.from(commands.value), {
    includeScore: true,
    includeMatches: true,
    findAllMatches: true,
    threshold: 0.95,
    keys: ['title', 'description', 'keywords'],
  });
  const results = fuse.search(query.value);
  const sortedResults = results.sort((a, b) => {
    return (a?.score ?? 0) - (b?.score ?? 0);
  });
  return sortedResults.map(result => result.item);
});

const displayedCommands = computed(() => {
  return query.value === '' ? commands.value : commandResults.value;
});

async function onSelect(command: Command): Promise<void> {
  await command.action(query.value);
  commandModalStore.hideCommandModal(props.modalId);
}

async function toggleCommandModal() {
  const isVisible = commandModalStore.toggleCommandModal(props.modalId);
  if (isVisible) {
    // Update what commands are available since there could be new conversations or similar.
    commands.value = await commandModalStore.reloadCommands();
  }
}

function handleKeydown(event: KeyboardEvent) {
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault();
    toggleCommandModal();
  }
  else if (event.key === 'Escape') {
    if (isModalVisible.value) {
      commandModalStore.hideCommandModal(props.modalId);
    }
  }
}

onMounted(() => {
  commandModalStore.mountCommandModal(props.modalId, plugins.value);
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  if (plugins.value) {
    commandModalStore.removePlugins(plugins.value);
  }
  commandModalStore.unmountCommandModal(props.modalId);
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <TransitionRoot
    :show="isModalVisible"
    as="template"
    appear
    @after-leave="query = ''"
  >
    <Dialog
      as="div"
      class="relative z-2000"
      :dismissable-mask="true"
      @close="() => commandModalStore.hideCommandModal(props.modalId)"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500/25 transition-opacity"
        />
      </TransitionChild>

      <div
        class="fixed inset-0 z-50 w-screen overflow-y-auto p-4 sm:p-6 md:p-20"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="mx-auto max-w-2xl transform divide-y divide-gray-500 divide-opacity-20 overflow-hidden rounded-xl bg-gray-900 shadow-2xl transition-all"
          >
            <Combobox @update:model-value="onSelect">
              <div class="relative">
                <ComboboxInput
                  class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-white focus:ring-0 sm:text-sm focus:outline-hidden"
                  :placeholder="t('commands.searchPlaceholder')"
                  @change="query = $event.target.value"
                />
              </div>

              <ComboboxOptions
                v-if="query === '' || commandResults.length > 0"
                :key="query"
                static
                class="max-h-80 scroll-py-2 divide-y divide-gray-500 divide-opacity-20 overflow-y-auto"
              >
                <li class="p-2">
                  <h2
                    v-if="query === ''"
                    class="mb-2 mt-4 px-3 text-xs font-semibold text-gray-200"
                  >
                    {{ t('commands.availableCommands') }}
                  </h2>
                  <ul class="text-sm text-gray-400">
                    <ComboboxOption
                      v-for="command in displayedCommands"
                      :key="command.id"
                      v-slot="{ active }"
                      :value="command"
                      as="template"
                    >
                      <li
                        class="flex cursor-default select-none items-center rounded-md px-3 py-2" :class="[
                          active && 'bg-gray-800 text-white',
                        ]"
                      >
                        <span class="ml-3 flex-auto truncate">{{
                          command.title
                        }}</span>
                        <span
                          v-if="active"
                          class="ml-3 flex-none text-gray-400"
                        >{{ t('commands.runAction') }}</span>
                      </li>
                    </ComboboxOption>
                  </ul>
                </li>
                <li
                  v-if="query === ''"
                  class="p-2"
                >
                  <ul class="text-sm text-gray-400">
                    <ComboboxOption
                      v-for="command in emptyQueryCommands"
                      :key="command.title"
                      v-slot="{ active }"
                      :value="command"
                      as="template"
                    >
                      <li
                        class="flex cursor-default select-none items-center rounded-md px-3 py-2" :class="[
                          active && 'bg-gray-800 text-white',
                        ]"
                      >
                        <span class="ml-3 flex-auto truncate">{{
                          command.title
                        }}</span>
                      </li>
                    </ComboboxOption>
                  </ul>
                </li>
              </ComboboxOptions>

              <div
                v-if="query !== '' && commandResults.length === 0"
                class="px-6 py-14 text-center sm:px-14"
              >
                <p class="mt-4 text-sm text-gray-200">
                  {{ t('commands.noResults') }}
                </p>
              </div>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
